/*  MUI  */
import { Box, Button, Paper, Typography } from '@mui/material';
import { BroadcastChannelContext } from 'application/App/BroadcastChannelContext';

/*  Shared  */
import { Logo } from 'application/Shared/Components/App';
import { ScopeTypes, URLs } from 'application/Shared/Constants';
import { Storage } from 'application/Shared/Handlers';
import { Helpers, Response } from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { Env, debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import Session from 'objects/Session';

/*  React  */
import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';

const baseURL = `${process.env.REACT_APP_BACKEND}/api`;

const buildUrl = (requestType, code, stateParam) => {
    const scopeType = stateParam.get('scope');
    const azureTenantId = stateParam.get('azureTenantId');
    const azureTenantParam =
        azureTenantId == null ? '' : `&azureTenantId=${azureTenantId}`;
    switch (scopeType) {
        case ScopeTypes.Impersonation:
            return `/authentication/oauth2/${requestType}/code/impersonate?code=${code}`;
        case ScopeTypes.AppAuth:
            return `/authentication/oauth2/${requestType}/code/appAuth?code=${code}${azureTenantParam}`;
        default:
            return `/authentication/oauth2/${requestType}/code?code=${code}`;
    }
};

class CloseOauth2 extends PureComponent {
    // eslint-disable-next-line react/static-property-placement
    static contextType = BroadcastChannelContext;

    constructor(props) {
        super(props);
        this.state = {
            error: false,
            errorMsg: null
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const { t } = this.props;
        if (Helpers.getQueryParams('not_support', window.location.href)) {
            this.setState({ error: true });
            this.setState({
                errorMsg: t('CloseOauth2.errorMsg.useCorporateAccount')
            });
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const stateUrlParams = new URLSearchParams(params.get('state'));

        if (params.get('error')) {
            this.setState({ error: true });
            return;
        }

        const code = Helpers.getQueryParams('code', window.location.href);
        const stateParam = Helpers.getQueryParams(
            'state',
            window.location.href
        );

        let requestType;
        if (stateParam.indexOf('google') > -1) {
            requestType = 'google';
        }
        if (stateParam.indexOf('microsoft') > -1) {
            requestType = 'microsoft';
        }

        try {
            // Only call withCredentials if it is for our application
            const scopeType = stateUrlParams.get('scope');
            const withCredentials =
                scopeType !== ScopeTypes.Impersonation &&
                scopeType !== ScopeTypes.AppAuth;

            const url = buildUrl(requestType, code, stateUrlParams);

            const api = axios.create({
                baseURL: withCredentials && Env.isLocal() ? '/api' : baseURL
            });
            const response = await api.get(url, {
                cancelToken: this.requests.token(),
                params: { redirectUri: `${window.origin}/close` },
                withCredentials
            });

            if (!Response.has(response)) {
                debug.e('No Response from Auth', response);
                this.setState({ error: true });
                setTimeout(() => window.close(), 2000);
                return;
            }

            const { data } = response;
            const {
                action,
                jwt,
                access_token: accessToken,
                ttlSeconds,
                scope,
                refresh_token: refreshToken
            } = data;
            const ttlMilliseconds = ttlSeconds ? ttlSeconds * 1000 : 3_600_000; // default to 1 hour.

            if (scopeType === ScopeTypes.Impersonation) {
                if (accessToken) {
                    Session.storeAccessToken(
                        accessToken,
                        ttlMilliseconds,
                        async () => {
                            window.close();
                        }
                    );
                } else {
                    this.setState({
                        errorMsg: t(
                            'CloseOauth2.errorMsg.noAccessTokenProvided'
                        )
                    });
                }

                return;
            }
            if (scopeType === ScopeTypes.AppAuth) {
                const azureTenantId = stateUrlParams.get('azureTenantId');
                if (accessToken && azureTenantId) {
                    Session.storeAdminAccessGrantedByTenant(
                        true,
                        azureTenantId,
                        async () => {
                            window.close();
                        }
                    );
                } else {
                    this.setState({
                        errorMsg: t(
                            'CloseOauth2.errorMsg.notoken_noTenantId_provided'
                        )
                    });
                }

                // GWS OAuth2
                if (requestType === 'google' && accessToken && scope) {
                    const {
                        googleAuth: {
                            scopeUser,
                            scopeGroup,
                            scopeOrgUnit,
                            scopeDomain,
                            scopeReportAudit
                        }
                    } = URLs;
                    const scopes = scope.split(' ');
                    const scopeMatches = [
                        scopeUser,
                        scopeGroup,
                        scopeOrgUnit,
                        scopeDomain,
                        scopeReportAudit
                    ].every((scopeLocal) => scopes.includes(scopeLocal));
                    Session.storeGoogleAppAuthDetails(
                        accessToken,
                        refreshToken,
                        scopeMatches,
                        async () => {
                            window.close();
                        }
                    );
                    debug.i(accessToken);
                    debug.i(scope);
                }

                return;
            }

            // It might be a way to have things continue to reload until the JWT becomes
            // available as it waits for the new tenant to be created ???
            if (action && action === 'NEW_TENANT') {
                debug.dev('Handling Action', action);
                await Session.storeJWT(action, async () => {
                    this.context.sendMessage('pageReload');
                    window.close();
                });
                return;
            }

            if (jwt) {
                await Session.storeJWT(jwt, async () => {
                    this.context.sendMessage('pageReload');
                    window.close();
                });
                Storage.set('authentication-type', requestType, (v) => {
                    if (!v) {
                        debug.e(
                            'Failed to store',
                            'authentication-type',
                            requestType
                        );
                    }
                });
                return;
            }

            debug.e('No JWT or Action from Auth', response);
            this.setState({ error: true });
            setTimeout(() => window.close(), 2000);
        } catch (e) {
            debug.e('Auth Error', e);
            if (Helpers.isset(e, 'response', 'data', 'error')) {
                this.setState({ errorMsg: e.response.data.error });
            }

            this.setState({ error: true });
            setTimeout(() => window.close(), 2000);
        }
    };

    unload = () => {
        this.requests.cancel();
    };

    render() {
        const { t } = this.props;
        const { error, errorMsg } = this.state;

        return (
            <Paper
                elevation={0}
                variant="outlined"
                style={{ height: '100%', padding: '24px' }}
            >
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box>
                        <Logo />
                    </Box>
                    <Box my="32px" textAlign="center">
                        {error ? (
                            <>
                                <hr />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'left'
                                    }}
                                >
                                    {errorMsg || t('OAuth.error')}
                                </Typography>
                                <Button
                                    data-trackid="login_closeOauth_close"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => window.close()}
                                    style={{ marginTop: '32px' }}
                                >
                                    {t('OAuth.close')}
                                </Button>
                            </>
                        ) : (
                            <Typography
                                variant="h3"
                                sx={{ color: 'text.tertiary' }}
                            >
                                {t('OAuth.wait')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        );
    }
}

export default withTranslation()(CloseOauth2);
